/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action, computed } from '@ember/object';
import { ONE_MINUTE_IN_SECONDS, ONE_HOUR_IN_SECONDS, ONE_DAY_IN_SECONDS } from 'cio/utils/time-constants';

import AnalyticsService from 'cio/services/analytics';
import MessageSettingsService from 'cio/services/message-settings';
import NewsletterModel from 'cio/models/newsletter';
import { tracked } from '@glimmer/tracking';
import EnvironmentService from 'cio/services/environment';

interface NewsletterSendingOptionsArgs {
  newsletter: NewsletterModel;
  sendRate: 'asap' | 'limit';
  readOnly: boolean;
  errors: any;
  setSendRate: () => unknown;
  validate: (prop: string) => unknown;
  onChangeUseMessageLimits: (useMessageLimit: boolean) => unknown;
  onAudienceChange: (subscriptionTopicId: string | null, sendToUnsubscribed: boolean) => unknown;
}

export default class NewsletterSendingOptions extends Component<NewsletterSendingOptionsArgs> {
  @service analytics!: AnalyticsService;
  @service messageSettings!: MessageSettingsService;
  @service environment!: EnvironmentService;

  get sendRatePeriodOptions() {
    return [
      { label: 'minute', value: ONE_MINUTE_IN_SECONDS },
      { label: 'hour', value: ONE_HOUR_IN_SECONDS },
      { label: 'day', value: ONE_DAY_IN_SECONDS },
    ];
  }

  @tracked hasTouchedLimitRate = false;
  @tracked hasTouchedTimePeriod = false;

  get isLimitRateDisabled() {
    return this.args.newsletter.tzMatchEnabled || this.args.newsletter.hasLanguageVariations;
  }

  get limitRateDisabledTooltipContent() {
    if (this.args.newsletter.hasLanguageVariations) {
      return 'You cannot limit the send rate of a Newsletter with languages added.';
    }

    if (this.args.newsletter.tzMatchEnabled) {
      return 'You cannot limit the send rate of a Newsletter when User Time Zone Match has been selected.';
    }

    return 'You cannot limit the send rate.';
  }

  @computed('args.errors.rateLimitTimePeriod.[]')
  get rateLimitTimePeriodErrors() {
    if ((this.args.errors?.rateLimitTimePeriod ?? []).length === 0) {
      return '';
    }

    return `${this.args.errors.rateLimitTimePeriod.join('. ')}.`;
  }

  @action
  onChangedLimitRate(value: string) {
    this.hasTouchedLimitRate = true;
    this.args.newsletter.set('rateLimitEmailRate', value);
    this.args.validate('rateLimitEmailRate');
  }

  @action
  onChangedTimePeriod() {
    this.hasTouchedTimePeriod = true;
    this.args.validate('rateLimitTimePeriod');
  }

  @action
  setTimePeriod(item: { value: string }) {
    this.args.newsletter.set('rateLimitTimePeriod', item.value);

    this.onChangedTimePeriod();
  }

  @action
  changeUseMessageLimits(useMessageLimit: boolean) {
    this.analytics.track('broadcast', 'edit_started', {
      editType: `include_in_message_limit_${useMessageLimit ? 'enabled' : 'disabled'}`,
      model: this.args.newsletter,
    });

    this.args.onChangeUseMessageLimits(useMessageLimit);
  }
}
